import type { VinDetail } from '@/api/schemas';
import { Field, LABEL_CLASS, VALUE_DEFAULT_CLASS } from '@/components/Field';
import { Panel } from '@/components/ui/panel';
import { SectionHeading } from '@/components/ui/section-heading';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { VehicleIdentityRow } from '@/components/vin/VehicleIdentityRow';
import { getFeatureRows } from '@/components/vin/vehicleFeatures';
import { NOT_AVAILABLE, orNotAvailable } from '@/lib/display';
import { DateText } from '@/lib/formatDate';

function FeatureColumn({ lines }: { lines: string[] }) {
  return (
    <p className={`${VALUE_DEFAULT_CLASS} self-center whitespace-pre-line`}>
      {lines.join('\n')}
    </p>
  );
}

function VehicleInfo({ detail }: { detail: VinDetail | null }) {
  return (
    <div className="mt-3">
      <SectionHeading className="h-9 px-4">Vehicle Info</SectionHeading>
      <Panel variant="data" size="compact">
        <div className="grid grid-cols-2 gap-x-4 gap-y-2 sm:grid-cols-4">
          <Field label="Condition" value={NOT_AVAILABLE} strong={false} />
          <Field label="Order Date" value={NOT_AVAILABLE} strong={false} />
          <Field
            label="Build Date"
            value={
              detail?.customerBuildDate ? (
                <DateText value={detail.customerBuildDate} />
              ) : (
                NOT_AVAILABLE
              )
            }
            strong={false}
          />
          <Field
            label="Application"
            value={orNotAvailable(detail?.application)}
            strong={false}
          />
          <Field
            label="Warranty Start Odometer"
            value={
              detail?.origWarrantyOdometer
                ? `${detail.origWarrantyOdometer}${detail.origWarrantyOdometerType ? ` ${detail.origWarrantyOdometerType}` : ''}`
                : NOT_AVAILABLE
            }
            strong={false}
          />
          <Field
            label="Start Engine Hrs"
            value={orNotAvailable(detail?.origWarrantyEngineHours)}
            strong={false}
          />
        </div>
      </Panel>
    </div>
  );
}

function Features({ detail }: { detail: VinDetail | null }) {
  return (
    <div className="mt-3">
      <SectionHeading className="h-9 px-4">Features</SectionHeading>
      <div className="max-h-[400px] overflow-y-auto rounded-t-[4px] border border-sheet-features-stroke shadow-[inset_0px_-3px_4px_0px_rgba(0,0,0,0.08)]">
        <Panel variant="data" size="roomy">
          <div className="grid grid-cols-[0.75fr_1fr_0.5fr] gap-2">
            {getFeatureRows(detail).map((row) => (
              <div key={row.label} className="contents">
                <p className={`${LABEL_CLASS} self-center`}>{row.label}</p>
                <FeatureColumn lines={row.col2} />
                <FeatureColumn lines={row.col3} />
              </div>
            ))}
          </div>
        </Panel>
      </div>
    </div>
  );
}

export function AdditionalVehicleSheet({
  open,
  onOpenChange,
  detail,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  detail: VinDetail | null;
}) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="p-0">
        <SheetHeader className="h-14 border-b border-separator px-4">
          <SheetTitle className="text-[rgba(0,0,0,0.87)]">
            Additional Vehicle Information
          </SheetTitle>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-4 pb-4 pt-3">
          <Panel variant="identity" size="snug">
            <VehicleIdentityRow detail={detail} compact />
          </Panel>
          <VehicleInfo detail={detail} />
          <Features detail={detail} />
        </div>
      </SheetContent>
    </Sheet>
  );
}
